import type { ReactNode } from "react";
import { cn } from "@/shared/lib/cn";
import { PageHeroAside } from "./PageHeroAside";
import { PageHeroCopy } from "./PageHeroCopy";

export interface PageHeroProps {
  /** El rótulo pequeño sobre el titular. */
  eyebrow: string;
  /** El titular completo, ya traducido. */
  title: string;
  /** La parte del titular que va resaltada. Debe estar dentro de `title`. */
  highlight?: string;
  description?: ReactNode;
  /** Los botones de la portada. */
  actions?: ReactNode;
  /**
   * La pieza viva de la derecha. Sin ella la portada se pinta a una sola
   * columna en vez de dejar medio ancho vacío.
   */
  aside?: ReactNode;
  className?: string;
}

/**
 * LA PORTADA DE PÁGINA del sitio: texto a la izquierda, algo vivo a la
 * derecha.
 *
 * ══════════════════════════════════════════════════════════════════════
 * EL SITIO TIENE DOS PATRONES DE PORTADA Y SOLO DOS (Anahi, 26-ago):
 *
 *   1. ESTE — contenido a la IZQUIERDA y una pieza viva a la DERECHA.
 *      El original es la sección de apertura de `/franquicias`: titular,
 *      precio, dos botones, y el vídeo circular flotando al lado.
 *   2. El HÉROE CON MEDIA DE FONDO — `features/shop/components/ShopHero`:
 *      el vídeo a sangre, el velo medido encima y el texto sobre él.
 *
 * Una portada nueva es uno de los dos. No hay un tercero, y "texto
 * centrado sobre fondo plano" —lo que era `/paquetes` hasta hoy— no es
 * ninguno.
 * ══════════════════════════════════════════════════════════════════════
 *
 * ── Por qué el patrón 1 vive aquí y no en `features/franchise` ───────
 * Porque `/franquicias` no lo puede prestar. Allí la portada es la
 * sección 0 de un `ScrollGlobe`: cuatro secciones de alto de pantalla y
 * una pieza `fixed` que VIAJA entre ellas mientras se desplaza. Eso no es
 * una portada, es una página entera con una portada dentro; montarlo en
 * `/paquetes` habría traído la navegación lateral, la barra de progreso y
 * un vídeo que sobrevuela la rejilla de paquetes.
 *
 * Lo que sí es reutilizable —la rejilla asimétrica, la columna de texto,
 * la caja de la derecha y sus reglas— es este archivo. `/franquicias`
 * sigue con el suyo porque el suyo hace algo más; el día que su portada
 * deje de ser sección de un scroll, se muda aquí.
 *
 * ── Fondo PLANO, y sin halo ──────────────────────────────────────────
 * `bg-ds-cream` a secas. Antes `/paquetes` llevaba aquí un halo lila
 * difuminado (`bg-ds-halo-lilac blur-3xl`) que era un `radial-gradient`;
 * se fue con el resto de degradados decorativos. Lo que separa la portada
 * de lo que viene debajo es el aire, no una mancha de color.
 *
 * ── La rejilla ───────────────────────────────────────────────────────
 * Una columna hasta `lg` y dos a partir de ahí. La de la derecha es
 * `auto`: la pieza viva mide lo que mide (un círculo de 340 px) y no debe
 * estirarse; la de la izquierda es `minmax(0,1fr)` y no `1fr` porque un
 * `1fr` no se encoge por debajo del contenido —un titular largo sin
 * espacios desbordaría la rejilla por la derecha en vez de partirse.
 */
export function PageHero({
  eyebrow,
  title,
  highlight,
  description,
  actions,
  aside,
  className,
}: PageHeroProps) {
  return (
    <section
      className={cn(
        "relative overflow-hidden bg-ds-cream pb-10 pt-12 sm:pb-14 sm:pt-20",
        className,
      )}
    >
      <div
        className={cn(
          "mx-auto grid max-w-6xl items-center gap-12 px-6 sm:px-8",
          aside && "lg:grid-cols-[minmax(0,1fr)_auto] lg:gap-16",
        )}
      >
        <PageHeroCopy
          eyebrow={eyebrow}
          title={title}
          highlight={highlight}
          description={description}
          actions={actions}
        />

        {aside && <PageHeroAside>{aside}</PageHeroAside>}
      </div>
    </section>
  );
}
